import type DxfArrayScanner from '../DxfArrayScanner';
import { serializeGroupValue } from '../DxfArrayScanner';
import {
  checkCommonEntityProperties,
  parsePoint,
  serializeCommonEntityProperty,
  serializePoint,
} from '../ParseHelpers';
import type IGeometry from './geometry';
import type { IEntity, IPoint } from './geometry';
import { EntityName } from './geometry';

export type ILeaderEntity = IEntity & {
  associatedAnnotationRef: number; //340
  blockOffset: IPoint; //212, 222, 232
  dimCLRD: number; //77
  dimensionStyle: string; //3
  extrusionDirection: IPoint; //210
  hasArrowHead: boolean; //71
  hasHookLine: boolean; //75
  hookLineDirectionFlag: number; //74
  horizontalDir: IPoint; //211, 221, 231
  isSpline: boolean; //72
  leaderCreationFlag: number; //73
  textHeight: number; //40
  textWidth: number; //41
  type: EntityName.Leader;
  vertexCount: number; //76
  vertices: IPoint[]; //10, 20, 30
  annotationOffset: IPoint; //213 , 223, 233
};

const leaderPropertyFromCode = new Map<number, keyof ILeaderEntity>([
  [3, 'dimensionStyle'],
  [40, 'textHeight'],
  [41, 'textWidth'],
  [73, 'leaderCreationFlag'],
  [74, 'hookLineDirectionFlag'],
  [76, 'vertexCount'],
  [77, 'dimCLRD'],
  [340, 'associatedAnnotationRef'],
]);

const codeFromLeaderProperty = new Map<keyof ILeaderEntity, number>(
  Array.from(leaderPropertyFromCode.entries()).map(([code, property]) => [
    property,
    code,
  ]),
);
//delete special cases that are are handled differently when serializing and parsing

export default class Leader implements IGeometry<ILeaderEntity> {
  public ForEntityName = EntityName.Leader;
  public parseEntity(scanner: DxfArrayScanner): ILeaderEntity {
    const entity = { type: this.ForEntityName } as ILeaderEntity;
    let curr = scanner.next();
    while (!scanner.isEOF()) {
      if (curr.code === 0) break;

      const property = leaderPropertyFromCode.get(curr.code);
      if (property != null) {
        (entity[property] as string | number) = curr.value as string | number;
      } else {
        //special cases
        switch (curr.code) {
          case 10: // X coordinate of point
            if (!entity.vertices)
              entity.vertices = [] as unknown as [IPoint, IPoint];
            entity.vertices.push(parsePoint(scanner));
            break;
          case 71:
            entity.hasArrowHead = !!curr.value;
            break;
          case 72:
            entity.isSpline = !!curr.value;
            break;
          case 75:
            entity.hasHookLine = !!curr.value;
            break;
          case 210:
            entity.extrusionDirection = parsePoint(scanner);
            break;
          case 211:
            entity.horizontalDir = parsePoint(scanner);
            break;
          case 212:
            entity.blockOffset = parsePoint(scanner);
            break;
          case 213:
            entity.annotationOffset = parsePoint(scanner);
            break;
          default:
            checkCommonEntityProperties(entity, curr, scanner);
            break;
        }
      }

      curr = scanner.next();
    }
    return entity;
  }

  public *serializeEntity(entity: ILeaderEntity): IterableIterator<string> {
    for (const [property, value] of Object.entries(entity) as [
      keyof ILeaderEntity,
      ILeaderEntity[keyof ILeaderEntity],
    ][]) {
      const code = codeFromLeaderProperty.get(property);
      if (code != null) {
        yield* serializeGroupValue(code, value as string | number | boolean);
      } else {
        switch (property) {
          case 'vertices':
            for (const vertex of value as IPoint[]) {
              yield* serializePoint(vertex, 10);
            }
            break;
          case 'hasArrowHead':
            yield '71';
            yield value ? '1' : '0';
            break;
          case 'isSpline':
            yield '72';
            yield value ? '1' : '0';
            break;
          case 'hasHookLine':
            yield '75';
            yield value ? '1' : '0';
            break;
          case 'extrusionDirection':
            yield* serializePoint(value as IPoint, 210);
            break;
          case 'horizontalDir':
            yield* serializePoint(value as IPoint, 211);
            break;
          case 'blockOffset':
            yield* serializePoint(value as IPoint, 212);
            break;
          case 'annotationOffset':
            yield* serializePoint(value as IPoint, 213);
            break;
          default:
            yield* serializeCommonEntityProperty(
              property,
              value as string | number | boolean,
              entity,
            );
            break;
        }
      }
    }
  }
}
